import React, { forwardRef } from "react";

import { NavLink, NavLinkProps } from "react-router";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Chip,
  Collapse,
  ListItemButton,
  ListItemProps,
  ListItemText,
  Tooltip,
} from "@mui/material";

import styled from "@emotion/styled";
import { darken, rgba } from "polished";

const CustomRouterLink = forwardRef<any, NavLinkProps>((props, ref) => (
  <div ref={ref}>
    <NavLink {...props} />
  </div>
));

type ItemType = {
  activeclassname?: string;
  onClick?: () => void;
  to?: string;
  component?: typeof NavLink;
  depth: number;
  isCollapsed?: boolean;
};

const Item = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== "isCollapsed" && prop !== "activeclassname" && prop !== "depth",
})<ItemType>`
  padding-top: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) =>
    props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) =>
    props.isCollapsed
      ? props.theme.spacing(2.75)
      : props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) =>
    props.isCollapsed
      ? props.theme.spacing(2.75)
      : props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  justify-content: ${(props) => (props.isCollapsed ? "center" : "flex-start")};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: ${(props) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) =>
      darken(0.03, props.theme.sidebar.background)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`;

type TitleType = {
  depth: number;
};

const Title = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== "depth",
})<TitleType>`
  margin: 0;
  span {
    color: ${(props) =>
      rgba(
        props.theme.sidebar.color,
        props.depth && props.depth > 0 ? 0.7 : 1,
      )};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`;

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`;

type SidebarNavListItemProps = ListItemProps & {
  depth: number;
  href: string;
  icon: React.FC;
  badge?: string;
  open?: boolean;
  title: string;
  isCollapsed?: boolean;
};

const SidebarNavListItem: React.FC<SidebarNavListItemProps> = (props) => {
  const {
    title,
    href,
    depth = 0,
    children,
    icon: Icon,
    badge,
    open: openProp = false,
    isCollapsed = false,
  } = props;

  const [open, setOpen] = React.useState(openProp);

  const handleToggle = () => {
    setOpen((state) => !state);
  };

  if (children && !isCollapsed) {
    return (
      <React.Fragment>
        <Item depth={depth} onClick={handleToggle} isCollapsed={isCollapsed}>
          {Icon && <Icon />}
          <Title depth={depth}>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>
        <Collapse in={open}>{children}</Collapse>
      </React.Fragment>
    );
  }

  const itemContent = (
    <Item
      depth={depth}
      component={CustomRouterLink}
      to={href}
      activeclassname="active"
      isCollapsed={isCollapsed}
    >
      {Icon && <Icon />}
      {!isCollapsed && (
        <Title depth={depth}>
          {title}
          {badge && <Badge label={badge} />}
        </Title>
      )}
    </Item>
  );

  if (isCollapsed) {
    return (
      <Tooltip title={title} placement="right" arrow>
        <span>{itemContent}</span>
      </Tooltip>
    );
  }

  return itemContent;
};

export default SidebarNavListItem;
